/**
 * 客服 API 服务
 * 打开/创建客服会话（会在后端触发新会话短信通知）
 */

import { apiService } from '@shared/services/api/ApiService';

const API_BASE = '/support';

export interface SupportConversation {
  id: string;
  user_id: string;
  last_message_at: string | null;
  sms_notified_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OpenConversationResult {
  conversation: SupportConversation;
  created: boolean;
}

export type SupportSenderRole = 'user' | 'agent';
export type SupportContentType = 'text' | 'image';

export interface SupportMessage {
  id: string;
  conversation_id: string;
  sender_role: SupportSenderRole;
  content_type: SupportContentType;
  content: string | null;
  image_url: string | null;
  delivered: boolean;
  created_at: string;
}

export const supportApi = {
  /**
   * 点击"联系我们"：打开或创建客服会话。
   * 若创建了新会话，后端会向客服手机号发送一次性短信通知。
   */
  async openConversation(): Promise<OpenConversationResult> {
    const response = await apiService.post(`${API_BASE}/conversations`);
    return response.data;
  },

  /**
   * 获取当前用户活跃会话
   */
  async getActiveConversation(): Promise<SupportConversation | null> {
    const response = await apiService.get(`${API_BASE}/conversations/active`);
    return response.data?.conversation || null;
  },

  /**
   * 获取当前用户客服未读数
   */
  async getUnread(): Promise<number> {
    const response = await apiService.get(`${API_BASE}/unread`);
    return response.data?.unread || 0;
  },

  /**
   * 标记当前用户客服会话已读
   */
  async markRead(): Promise<void> {
    await apiService.post(`${API_BASE}/read`);
  },

  /**
   * 获取会话历史消息
   */
  async getMessages(conversationId: string, before?: string, limit = 50): Promise<SupportMessage[]> {
    const params = new URLSearchParams();
    if (before) params.set('before', before);
    params.set('limit', String(limit));
    const response = await apiService.get(
      `${API_BASE}/conversations/${conversationId}/messages?${params.toString()}`
    );
    return response.data?.messages || [];
  },

  /**
   * 上传聊天图片，返回图片引用 URL
   */
  async uploadImage(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('image', file);
    const response = await apiService.post(`${API_BASE}/upload/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data?.imageUrl;
  },
};
